import {
  Addon,
  EntityType,
  Subscription,
} from "../../api/grpc/subscription/subscription";
import { hasUnlimitedUsersAddon } from "../../lib/helpers/hasUnlimitedUsersAddon";
import { getAddonsName } from "../Billings/lib/getAddonsName";
import {
  getSubscriptionAddons,
  getSubscriptionItems,
} from "../Billings/lib/getSubscriptionItems";
import { SubscriptionTerm } from "./ProductStore.types";

export interface ProductStoreSubscriptionSummary {
  plan: string;
  period: string;
  /** e.g. ["10 Rooms", "250 Users", "1 Add-on", "Standard support"] */
  details: string[];
}

const PLAN_LABELS: Record<string, string> = {
  basic: "BASIC",
  business: "BUSINESS",
  businessplus: "BusinessPLUS",
  pro: "PRO",
};

const getPlanLabel = (subscription: Subscription) => {
  const rawPlan = subscription.name || subscription.planId;
  const knownPlan = rawPlan.match(/businessplus|business|basic|pro/i)?.[0];

  if (knownPlan) {
    return PLAN_LABELS[knownPlan.toLowerCase()];
  }

  return rawPlan.split(/[-\s]/)[0]?.toUpperCase() || "CURRENT PLAN";
};

/** Whether the subscription is on the given plan, e.g. "BusinessPLUS". */
export const isOnPlan = (subscription: Subscription, plan: string) =>
  getPlanLabel(subscription) === plan;

/** The subscription's term, or undefined when it can't be determined. */
export const getSubscriptionTerm = (
  subscription: Subscription
): SubscriptionTerm | undefined => {
  const period = `${subscription.payPeriod} ${subscription.name}`.toLowerCase();

  if (period.includes("1-year") || period.includes("yearly")) {
    return "1-year";
  }

  if (period.includes("3-year") || period.includes("3 year")) {
    return "3-year";
  }

  return undefined;
};

const PERIOD_LABELS: Record<SubscriptionTerm, string> = {
  "1-year": "1 year",
  "3-year": "3 years",
};

const getPeriodLabel = (subscription: Subscription) => {
  const term = getSubscriptionTerm(subscription);

  return term ? PERIOD_LABELS[term] : subscription.payPeriod || "Current term";
};

export const getProductStoreSubscriptionSummary = (
  subscription: Subscription
): ProductStoreSubscriptionSummary => {
  const hasUnlimitedUsers = hasUnlimitedUsersAddon(subscription.addons);

  // One total per item type, e.g. two room add-ons (6 + 4) read "10 Rooms".
  const totals = new Map<EntityType, Addon>();
  getSubscriptionItems(subscription.addons).forEach((item) => {
    const total = totals.get(item.entityType);
    totals.set(
      item.entityType,
      total ? { ...total, quantity: total.quantity + item.quantity } : item
    );
  });

  const items = Array.from(totals.values()).map((item) => {
    const isUnlimited =
      hasUnlimitedUsers && item.entityType === EntityType.USER;
    // Unlimited reads as a plural ("Unlimited Users").
    const name = getAddonsName(
      item.name.toLocaleLowerCase(),
      isUnlimited ? 2 : item.quantity,
      item.entityType
    );

    return `${isUnlimited ? "Unlimited" : item.quantity} ${name}`;
  });

  const addonCount = getSubscriptionAddons(subscription.addons).length;
  const hasTotalCare = subscription.addons.some(
    (addon) => addon.entityType === EntityType.TOTAL_CARE_SUPPORT
  );

  return {
    plan: getPlanLabel(subscription),
    period: getPeriodLabel(subscription),
    details: [
      ...items,
      ...(addonCount > 0
        ? [`${addonCount} ${addonCount === 1 ? "Add-on" : "Add-ons"}`]
        : []),
      hasTotalCare ? "Total Care Support" : "Standard support",
    ],
  };
};
